import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container } from 'components/elements';
import {
  GetAllMessagesQueryVariables,
  useAddMessageToFolderMutation,
  useCreateFolderMutation,
  useGetAllTelegramGroupsQuery,
  useGetAllTelegramMessagesLazyQuery,
  useGetMyFoldersQuery,
  useRemoveFolderMutation,
  useRemoveMessageFromFolderMutation,
  useLoggedInStateQuery,
  useGetMyAccountQuery,
} from 'graphql/types';
import {
  CommunityHero,
  CommunityLayout,
  CommunityPage,
  CommunityTitle,
  FeedColumn,
  SidebarColumn,
} from './index.styled';
import {
  CommunityGroup,
  CommunityMessage,
  Folder,
} from './types';
import FolderSidebar from './FolderSidebar';
import MessageFeed from './MessageFeed';
import { useHistory, useLocation } from 'react-router-dom';
import qs from 'query-string';

const pageSize = 10;

export default function CommunityView() {
  const [selectedGroupId, setSelectedGroupId] = useState('');
  const [searchValue, setSearchValue] = useState('');
  const [addMessageToFolderMutation] = useAddMessageToFolderMutation();
  const [removeMessageFromFolderMutation] = useRemoveMessageFromFolderMutation();
  const [removeFolderMutation] = useRemoveFolderMutation();
  const [createFolderMutation] = useCreateFolderMutation();
  const { data: loggedInData } = useLoggedInStateQuery();
  const { data: myFoldersResult, refetch: refetchMyFolders } = useGetMyFoldersQuery({
    skip: !loggedInData?.loggedInState,
  });
  const { data: myAccountResult } = useGetMyAccountQuery({
    skip: !loggedInData?.loggedInState,
  });
  const isLoggedIn = Boolean(loggedInData?.loggedInState);
  const currentUserId = myAccountResult?.myAccount?.data?.id
    ? String(myAccountResult.myAccount.data.id)
    : undefined;
  const [folders, setFolders] = useState<Folder[]>([]);
  const [messageFolderMap, setMessageFolderMap] = useState<Record<string, string>>({});
  const [newFolderName, setNewFolderName] = useState('');
  const [assigningMessageId, setAssigningMessageId] = useState<string | null>(null);
  const [commentCounts, setCommentCounts] = useState<Record<string, number>>({});
  const history = useHistory();
  const { search } = useLocation();
  const searchQuery = useMemo(() => qs.parse(search, { arrayFormat: 'bracket' }), [search]);
  const isTranslateEnabled = `${searchQuery.translate ?? '1'}` === '1';
  const selectedFolderId = (searchQuery.folderId as string) || 'all';
  const hasMountedRef = useRef(false);

  useEffect(() => {
    if (!hasMountedRef.current) {
      hasMountedRef.current = true;
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [searchQuery.folderId, searchQuery.groupId]);

  useEffect(() => {
    const nextFolders: Folder[] = (myFoldersResult?.myFolders ?? [])
      .filter((folder): folder is NonNullable<typeof folder> => Boolean(folder))
      .map((folder) => ({
        id: String(folder.id ?? ''),
        name: folder.name ?? 'Folder',
      }));

    setFolders(nextFolders);

    const nextMap: Record<string, string> = {};
    (myFoldersResult?.myFolders ?? []).forEach((folder) => {
      if (!folder?.id) return;
      const folderId = String(folder.id);
      folder.folderMessages?.data?.forEach((message) => {
        if (message?.id) {
          nextMap[String(message.id)] = folderId;
        }
      });
    });
    setMessageFolderMap(nextMap);
  }, [myFoldersResult]);

  const handleCreateFolder = useCallback(async () => {
    const name = newFolderName.trim();
    if (!name) return;

    try {
      const { data } = await createFolderMutation({ variables: { name } });
      const createdFolder = data?.createFolder?.data;
      const createdId = String(createdFolder?.id ?? Date.now().toString(36));
      const createdName = createdFolder?.attributes?.name ?? name;

      setFolders((prev) => {
        if (prev.some((folder) => folder.id === createdId)) {
          return prev;
        }

        return [...prev, { id: createdId, name: createdName }];
      });

      setNewFolderName('');
    } catch (error) {
      // Keep existing behavior intact if the API call fails.
      const fallbackFolders: Folder[] = [...folders, { id: Date.now().toString(36), name }];
      setFolders(fallbackFolders);
      setNewFolderName('');
      console.error('Failed to create folder via API', error);
    }
  }, [createFolderMutation, folders, newFolderName]);

  const handleAssignFolder = useCallback(
    async (messageId: string, folderId: string | null) => {
      const next = { ...messageFolderMap };
      if (folderId !== null) {
        try {
          await addMessageToFolderMutation({
            variables: {
              folderId,
              messageId,
            },
          });
          next[messageId] = folderId;
          await refetchMyFolders();
        } catch (error) {
          console.error('Failed to assign message to folder via API', error);
          return;
        }
      } else {
        try {
          const currentFolderId = messageFolderMap[messageId];
          if (!currentFolderId) {
            delete next[messageId];
            setMessageFolderMap(next);
            setAssigningMessageId(null);
            return;
          }

          await removeMessageFromFolderMutation({
            variables: {
              folderId: currentFolderId,
              messageId,
            },
          });
          delete next[messageId];
          await refetchMyFolders();
        } catch (error) {
          console.error('Failed to remove message from folder via API', error);
          return;
        }
      }

      setMessageFolderMap(next);
      setAssigningMessageId(null);
    },
    [addMessageToFolderMutation, removeMessageFromFolderMutation, messageFolderMap, refetchMyFolders],
  );

  const handleDeleteFolder = useCallback(
    async (folderId: string) => {
      try {
        await removeFolderMutation({
          variables: {
            folderId,
          },
        });

        const nextFolders = folders.filter((f) => f.id !== folderId);
        setFolders(nextFolders);
        const nextMap = { ...messageFolderMap };
        Object.keys(nextMap).forEach((mid) => {
          if (nextMap[mid] === folderId) delete nextMap[mid];
        });
        setMessageFolderMap(nextMap);
        if (selectedFolderId === folderId) {
          history.push(
            `/community?${qs.stringify(
              { ...searchQuery, page: 1, groupId: selectedGroupId, folderId: undefined },
              { arrayFormat: 'bracket', skipNull: true, skipEmptyString: true },
            )}`,
          );
        }
      } catch (error) {
        console.error('Failed to remove folder via API', error);
        return;
      }
    },
    [folders, history, messageFolderMap, removeFolderMutation, searchQuery, selectedFolderId, selectedGroupId],
  );
  const { data: groupsResult, loading: groupsLoading } = useGetAllTelegramGroupsQuery({
    variables: { sort: ['order:asc'] },
    fetchPolicy: 'no-cache',
  });

  // const { data: messagesResult, loading: messagesLoading } = useGetAllTelegramMessagesQuery({
  //   variables: { sort: ['date:desc'] },
  // });
  const [getMessages, { data: messagesResult, loading: messagesLoading }] = useGetAllTelegramMessagesLazyQuery();

  const getMessagesPage = useCallback(
    (page: number, groupId?: string, translate?: number, folderId?: string) => {
      const searchFilter = { sort: 'createdAt:desc', pagination: { page, pageSize } } as GetAllMessagesQueryVariables;
      const searchFilterProps = {} as Record<string, unknown>;
      const effectiveGroupId = groupId || (searchQuery?.groupId as string);
      const effectiveTranslate = typeof translate === 'number' ? translate : Number(searchQuery?.translate ?? 1);
      const effectiveFolderId = folderId ?? ((searchQuery?.folderId as string) || 'all');

      if (effectiveGroupId) {
        searchFilterProps.telegram_group = { id: { eq: effectiveGroupId } };
      }
      if (effectiveFolderId && effectiveFolderId !== 'all') {
        searchFilterProps.folder = { id: { eq: effectiveFolderId } };
      }

      getMessages({ variables: { ...searchFilter, filters: searchFilterProps, translate: effectiveTranslate } });
    },
    [getMessages, searchQuery],
  );

  const groups = useMemo<CommunityGroup[]>(
    () =>
      (groupsResult?.telegramGroups?.data ?? []).map((group) => ({
        id: String(group?.id ?? ''),
        title: group?.attributes?.title ?? 'Telegram Group',
        type: group?.attributes?.type ?? 'group',
        coverPhotoUrl: group?.attributes?.coverPhotoUrl ?? undefined,
      })),
    [groupsResult],
  );

  const updatePageParams = useCallback(
    (searchParams: any) => {
      if (searchParams.groupId !== selectedGroupId) {
        setSelectedGroupId(searchParams.groupId);
      }
      history.push(`/community?${qs.stringify(searchParams, { arrayFormat: 'bracket', skipNull: true, skipEmptyString: true })}`);
    },
    [history, selectedGroupId],
  );

  const handleSelectFolder = useCallback(
    (folderId: string) => {
      updatePageParams({
        ...searchQuery,
        page: 1,
        groupId: selectedGroupId,
        folderId: folderId === 'all' ? undefined : folderId,
      });
    },
    [searchQuery, selectedGroupId, updatePageParams],
  );

  useEffect(() => {
    const groupIdFromQuery = (searchQuery.groupId as string) || '';
    if (groupIdFromQuery) {
      setSelectedGroupId(groupIdFromQuery);
      return;
    }

    if (!selectedGroupId && groups.length > 0) {
      const initialGroupId = groups[0].id;
      updatePageParams({ ...searchQuery, groupId: initialGroupId, page: 1 });
    }
  }, [groups, searchQuery, selectedGroupId, updatePageParams]);

  useEffect(() => {
    const groupIdFromQuery = (searchQuery.groupId as string) || selectedGroupId || groups[0]?.id;
    if (!groupIdFromQuery) return;
    const queryPage = Number(searchQuery.page || 1);
    const page = Number.isFinite(queryPage) && queryPage > 0 ? queryPage : 1;
    const folderId = (searchQuery.folderId as string) || 'all';
    getMessagesPage(page, groupIdFromQuery, isTranslateEnabled ? 1 : 0, folderId);
  }, [
    getMessagesPage,
    groups,
    isTranslateEnabled,
    searchQuery.groupId,
    searchQuery.page,
    searchQuery.folderId,
    selectedGroupId,
  ]);

  const groupTitleMap = useMemo(
    () =>
      groups.reduce<Record<string, string>>((acc, group) => {
        acc[group.id] = group.title;
        return acc;
      }, {}),
    [groups],
  );

  const messages = useMemo<CommunityMessage[]>(
    () =>
      (messagesResult?.telegramMessagesTranslated?.data ?? []).map((message) => {
        const originalContent = message.attributes?.content?.trim() || '';
        const translatedContent = message.attributes?.translated_content?.trim() || '';

        return {
          id: String(message.id ?? ''),
          groupId: String(message.attributes?.telegram_group?.data?.id ?? ''),
          senderName: message.attributes?.sender_name ?? 'Telegram User',
          content: (isTranslateEnabled ? translatedContent : originalContent) || originalContent,
          originalContent,
          translated_content: translatedContent,
          date: message.attributes?.date ?? '',
          commentsCount:
            commentCounts[String(message.id ?? '')] ?? message.attributes?.commentsCount ?? 0,
        };
      }),
    [commentCounts, isTranslateEnabled, messagesResult],
  );

  const filteredMessages = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    return messages.filter((message) => {
      const matchesFolder =
        selectedFolderId === 'all' || messageFolderMap[message.id] === selectedFolderId;
      const matchesQuery =
        query.length === 0 ||
        message.content.toLowerCase().includes(query) ||
        message.senderName.toLowerCase().includes(query) ||
        (groupTitleMap[message.groupId] ?? '').toLowerCase().includes(query);

      return matchesFolder && matchesQuery;
    });
  }, [groupTitleMap, messageFolderMap, messages, searchValue, selectedFolderId]);

  const pagination = messagesResult?.telegramMessagesTranslated?.meta?.pagination;
  const currentPage = Number(searchQuery.page) || pagination?.page || 1;

  return (
    <CommunityPage>
      <Container>
        <CommunityHero>
          <CommunityTitle>Goftemān</CommunityTitle>
        </CommunityHero>

        <CommunityLayout>
          <FeedColumn>
            <MessageFeed
              loading={groupsLoading || messagesLoading}
              filteredMessages={filteredMessages}
              totalMessages={pagination?.total || 0}
              currentPage={currentPage}
              pageSize={pagination?.pageSize || pageSize}
              searchValue={searchValue}
              onSearchChange={setSearchValue}
              onPageChange={(page) =>
                updatePageParams({
                  ...searchQuery,
                  page,
                  groupId: selectedGroupId,
                  folderId: selectedFolderId === 'all' ? undefined : selectedFolderId,
                })
              }
              isTranslateEnabled={isTranslateEnabled}
              onToggleTranslate={() =>
                updatePageParams({
                  ...searchQuery,
                  groupId: selectedGroupId,
                  page: 1,
                  translate: isTranslateEnabled ? 0 : 1,
                })
              }
              groups={groups}
              selectedGroupId={selectedGroupId}
              onSelectGroup={(id) => {
                updatePageParams({ ...searchQuery, groupId: id, page: 1 });
              }}
              folders={folders}
              messageFolderMap={messageFolderMap}
              assigningMessageId={assigningMessageId}
              onTogglePicker={(id) => setAssigningMessageId((prev) => (prev === id ? null : id))}
              onClosePicker={() => setAssigningMessageId(null)}
              onAssignFolder={handleAssignFolder}
              isLoggedIn={isLoggedIn}
              currentUserId={currentUserId}
              onCommentsCountChange={(messageId, count) =>
                setCommentCounts((prev) => ({ ...prev, [messageId]: count }))
              }
              selectedFolderId={selectedFolderId}
            />
          </FeedColumn>

          <SidebarColumn>
            <FolderSidebar
              folders={folders}
              selectedFolderId={selectedFolderId}
              newFolderName={newFolderName}
              onNewFolderNameChange={setNewFolderName}
              onCreateFolder={handleCreateFolder}
              onSelectFolder={handleSelectFolder}
              onDeleteFolder={handleDeleteFolder}
            />
          </SidebarColumn>
        </CommunityLayout>
      </Container>
    </CommunityPage>
  );
}